import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import axios from "./axiosConfig";
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';

/** Valores sugeridos en el formulario crear/editar investigador */
const TIPO_PUESTO_OPCIONES_BASE = [
  "Profesor Tipo Completo",
  "Profesor Tipo Parcial",
  "Analista de Difusion de la Ciencia",
  "Analista de Gestion de Proyectos de Investigacion",
];

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeTipoPuesto(value) {
  return normalizeText(value);
}

function normalizeOpenAlexAuthorId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const withoutUrl = raw.replace(/^https?:\/\/openalex\.org\//i, "");
  return withoutUrl.toUpperCase();
}

function hasOpenAlexIdentifier(inv) {
  if (normalizeOpenAlexAuthorId(inv?.open_alex_id)) return true;
  if (!Array.isArray(inv?.open_alex_ids)) return false;
  return inv.open_alex_ids.some((id) => Boolean(normalizeOpenAlexAuthorId(id)));
}

function getDepartmentCampus(departamento) {
  const normalized = normalizeText(departamento);
  if (!normalized) return "Matriz";
  if (/\bltga\b/.test(normalized) || normalized.includes("latacunga")) return "Sede Latacunga";
  if (
    /\bsd\b/.test(normalized) ||
    normalized.includes("santo domingo") ||
    normalized.includes("tsachilas")
  ) {
    return "Sede Santo Domingo";
  }
  return "Matriz";
}

function getDepartmentDisplayName(departamento) {
  const raw = String(departamento || "").trim();
  if (!raw) return "Sin departamento";

  const normalized = normalizeText(raw);
  if (normalized === "sede latacunga") return "General";
  if (
    normalized === "sede santo domingo de los tsachilas" ||
    normalized === "sede santo domingo"
  ) {
    return "General";
  }

  return raw.replace(/\s+LTGA$/i, "").replace(/\s+SD$/i, "").trim();
}

function findUniversityForInvestigator(inv, universidades) {
  if (!inv) return null;

  if (inv.universidad_id != null) {
    const byId = universidades.find((u) => Number(u.id) === Number(inv.universidad_id));
    if (byId) return byId;
  }

  const institutionName = normalizeText(inv.institucion);
  if (!institutionName) return null;

  return (
    universidades.find((u) => normalizeText(u.name) === institutionName) ||
    universidades.find((u) => normalizeText(u.short_name) === institutionName) ||
    universidades.find((u) => normalizeText(u.siglas) === institutionName) ||
    null
  );
}

function getPublicationCount(inv) {
  const rawCount = Number(inv?.works_count);
  return Number.isFinite(rawCount) ? rawCount : 0;
}

function Investigadores() {
  const navigate = useNavigate();
  const { canCreateEdit } = useAuth();
  const { confirm } = useConfirm();
  const [investigadores, setInvestigadores] = useState([]);
  const [filteredInvestigadores, setFilteredInvestigadores] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const mainPageSize = 12;
  
  // Filtros
  const [selectedInstitution, setSelectedInstitution] = useState("");
  const [selectedGeneros, setSelectedGeneros] = useState([]);
  const [selectedTipoPuesto, setSelectedTipoPuesto] = useState([]);
  const [selectedDepartamento, setSelectedDepartamento] = useState([]);
  const [selectedPublicationStatus, setSelectedPublicationStatus] = useState("");
  
  // Estados para modales
  const [showModal, setShowModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedPublicaciones, setSelectedPublicaciones] = useState([]);
  const [modalPage, setModalPage] = useState(0);
  const pageSize = 9;
  const [selectedInvestigador, setSelectedInvestigador] = useState(null);
  const [deletingPublicationId, setDeletingPublicationId] = useState(null);
  const [investigadorProfile, setInvestigadorProfile] = useState(null);
  const [loadingInvestigadorProfile, setLoadingInvestigadorProfile] = useState(false);
  
  // Estados para formulario
  const [newInvestigador, setNewInvestigador] = useState({
    nombre: "",
    genero: "",
    institucion: "",
    universidad_id: "",
    open_alex_id: "",
    orcid: "",
    email: "",
    tipo_puesto: "",
    departamento: "",
  });
  const [universidadesApi, setUniversidadesApi] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState(null);
  
  // Vista de grid/lista
  const [viewMode, setViewMode] = useState("grid"); // "grid" o "list"
  const [sortBy, setSortBy] = useState("nombre");

  useEffect(() => {
    fetchInvestigadores();
  }, []);

  useEffect(() => {
    axios
      .get("/api/universities")
      .then((res) => setUniversidadesApi(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUniversidadesApi([]));
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedInstitution, selectedGeneros, selectedTipoPuesto, selectedDepartamento, selectedPublicationStatus, investigadores, sortBy]);

  useEffect(() => {
    setSelectedGeneros([]);
    setSelectedTipoPuesto([]);
    setSelectedDepartamento([]);
  }, [selectedInstitution]);

  const fetchInvestigadores = async () => {
    try {
      const response = await axios.get("/api/investigadores/", {
        params: { skip: 0, limit: 100000 },
      });
      if (Array.isArray(response.data)) {
        setInvestigadores(response.data);
        setFilteredInvestigadores(response.data);
      } else {
        setInvestigadores([]);
        setFilteredInvestigadores([]);
      }
    } catch (error) {
      setError("Error al obtener investigadores: " + error.message);
    }
  };

  const applyFilters = () => {
    let filtered = [...investigadores];
    
    // Filtro por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((inv) =>
        inv.nombre.toLowerCase().includes(term) ||
        (inv.institucion && inv.institucion.toLowerCase().includes(term)) ||
        (inv.departamento && inv.departamento.toLowerCase().includes(term)) ||
        (inv.tipo_puesto && inv.tipo_puesto.toLowerCase().includes(term))
      );
    }
    
    // Filtro por institución
    if (selectedInstitution) {
      filtered = filtered.filter((inv) => inv.institucion === selectedInstitution);
    }

    // Filtro por género
    if (selectedGeneros.length > 0) {
      filtered = filtered.filter((inv) => selectedGeneros.includes(inv.genero));
    }
    
    // Filtro por tipo de puesto
    if (selectedTipoPuesto.length > 0) {
      const selectedTipoPuestoKeys = new Set(
        selectedTipoPuesto.map((tipo) => normalizeTipoPuesto(tipo))
      );
      filtered = filtered.filter((inv) =>
        selectedTipoPuestoKeys.has(normalizeTipoPuesto(inv.tipo_puesto))
      );
    }
    
    // Filtro por departamento
    if (selectedDepartamento.length > 0) {
      filtered = filtered.filter((inv) => selectedDepartamento.includes(inv.departamento));
    }

    // Filtro por publicaciones
    if (selectedPublicationStatus === "con") {
      filtered = filtered.filter((inv) => getPublicationCount(inv) > 0);
    } else if (selectedPublicationStatus === "sin") {
      filtered = filtered.filter((inv) => getPublicationCount(inv) === 0);
    }
    
    // Ordenamiento
    filtered.sort((a, b) => {
      if (sortBy === "nombre") {
        return a.nombre.localeCompare(b.nombre);
      } else if (sortBy === "institucion") {
        return (a.institucion || "").localeCompare(b.institucion || "");
      } else if (sortBy === "tipo_puesto") {
        return (a.tipo_puesto || "").localeCompare(b.tipo_puesto || "");
      } else if (sortBy === "departamento") {
        return (a.departamento || "").localeCompare(b.departamento || "");
      }
      return 0;
    });
    
    setFilteredInvestigadores(filtered);
    setCurrentPage(0);
  };

  const institutionSource = universidadesApi.length > 0
    ? universidadesApi.map((u) => u.name || u.short_name).filter(Boolean)
    : [...new Set(investigadores.map((inv) => inv.institucion).filter(Boolean))];

  const institutionCounts = institutionSource
    .map((inst) => ({
      name: inst,
      count: investigadores.filter((inv) => inv.institucion === inst).length,
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const investigatorsForSelectedInstitution = selectedInstitution
    ? investigadores.filter((inv) => inv.institucion === selectedInstitution)
    : investigadores;

  // Obtener tipos de puesto únicos, agrupando variantes por mayúsculas/acentos
  const tipoPuestoMap = new Map();
  investigatorsForSelectedInstitution.forEach((inv) => {
    const rawTipoPuesto = String(inv.tipo_puesto || "").trim();
    if (!rawTipoPuesto) return;

    const key = normalizeTipoPuesto(rawTipoPuesto);
    if (!tipoPuestoMap.has(key)) {
      tipoPuestoMap.set(key, { name: rawTipoPuesto, count: 0 });
    }
    tipoPuestoMap.get(key).count += 1;
  });

  const tipoPuestoCounts = Array.from(tipoPuestoMap.values()).sort((a, b) => b.count - a.count);

  const tipoPuestoOpcionesFormulario = Array.from(
    [...TIPO_PUESTO_OPCIONES_BASE, ...investigadores.map((inv) => inv.tipo_puesto).filter(Boolean)].reduce(
      (map, value) => {
        const label = String(value || "").trim();
        if (!label) return map;

        const key = normalizeTipoPuesto(label);
        if (!map.has(key)) {
          map.set(key, label);
        }
        return map;
      },
      new Map()
    )
  )
    .map(([, label]) => label)
    .sort((a, b) => a.localeCompare(b));
  
  const departamentoGroupsMap = new Map();
  investigatorsForSelectedInstitution.forEach((inv) => {
    const rawDepartamento = String(inv.departamento || "").trim();
    if (!rawDepartamento) return;

    const campus = getDepartmentCampus(rawDepartamento);
    if (!departamentoGroupsMap.has(campus)) {
      departamentoGroupsMap.set(campus, []);
    }

    const items = departamentoGroupsMap.get(campus);
    const existing = items.find((item) => item.name === rawDepartamento);
    if (existing) {
      existing.count += 1;
      return;
    }

    items.push({
      name: rawDepartamento,
      label: getDepartmentDisplayName(rawDepartamento),
      count: 1,
    });
  });

  const campusOrder = ["Matriz", "Sede Latacunga", "Sede Santo Domingo"];
  const departamentoGroups = campusOrder
    .filter((campus) => departamentoGroupsMap.has(campus))
    .map((campus) => ({
      campus,
      items: departamentoGroupsMap
        .get(campus)
        .slice()
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    }));
  
  const generoMap = new Map();
  investigatorsForSelectedInstitution.forEach((inv) => {
    const genero = String(inv.genero || "").trim() || "No especificado";
    generoMap.set(genero, (generoMap.get(genero) || 0) + 1);
  });

  const generoCounts = Array.from(generoMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const handleInstitutionFilter = (institution) => {
    setSelectedInstitution((prev) => (prev === institution ? "" : institution));
  };

  const handleTipoPuestoFilter = (tipo) => {
    setSelectedTipoPuesto(prev =>
      prev.includes(tipo)
        ? prev.filter(t => t !== tipo)
        : [...prev, tipo]
    );
  };
  
  const handleDepartamentoFilter = (dep) => {
    setSelectedDepartamento(prev =>
      prev.includes(dep)
        ? prev.filter(d => d !== dep)
        : [...prev, dep]
    );
  };

  const handleGenderFilter = (genero) => {
    setSelectedGeneros((prev) =>
      prev.includes(genero) ? prev.filter((g) => g !== genero) : [...prev, genero]
    );
  };

  const publicationStatusCounts = {
    con: investigatorsForSelectedInstitution.filter((inv) => getPublicationCount(inv) > 0).length,
    sin: investigatorsForSelectedInstitution.filter((inv) => getPublicationCount(inv) === 0).length,
  };

  const fetchPublicacionesByInvestigador = async (inv) => {
    try {
      setSelectedInvestigador(inv);
      const response = await axios.get(`/api/investigadores/${inv.id}/publicaciones`);
      const sorted = Array.isArray(response.data)
        ? response.data.slice().sort((a, b) => (b.anio || 0) - (a.anio || 0))
        : [];
      setSelectedPublicaciones(sorted);
      setModalPage(0);
      setShowModal(true);
    } catch (error) {
      setError("Error al obtener publicaciones: " + error.message);
    }
  };

  const handleInputChange = (e) => {
    setNewInvestigador({ ...newInvestigador, [e.target.name]: e.target.value });
  };

  const handleAddPublication = (inv) => {
    if (!hasOpenAlexIdentifier(inv)) {
      setError("El investigador debe tener un OpenAlex ID antes de añadir publicaciones.");
      return;
    }

    const university = findUniversityForInvestigator(inv, universidadesApi);
    if (!university) {
      setError("No se pudo identificar la universidad del investigador para abrir publicaciones extra.");
      return;
    }

    setShowModal(false);
    navigate('/busqueda-publicaciones', {
      state: {
        prefillExtra: {
          investigadorId: inv.id,
          investigador: inv,
          universidadId: university.id,
          universidadName: university.name,
          authorName: inv.nombre,
        },
      },
    });
  };

  const handleDeletePublication = async (pub) => {
    if (!pub?.id) return;
    const accepted = await confirm({
      title: 'Eliminar publicación',
      message: `¿Deseas eliminar la publicación "${pub.titulo}"?`,
      details: 'Esta acción quitará la publicación del investigador y no se puede deshacer.',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      variation: 'danger',
      badge: 'Eliminar',
      icon: 'delete',
    });
    if (!accepted) return;

    try {
      setDeletingPublicationId(pub.id);
      await axios.delete(`/api/publicaciones/${pub.id}`);
      setSelectedPublicaciones((prev) => prev.filter((item) => item.id !== pub.id));
    } catch (error) {
      setError("Error al eliminar publicación: " + error.message);
    } finally {
      setDeletingPublicationId(null);
    }
  };

  const handleOpenInvestigadorProfile = async (inv) => {
    if (!inv?.id) return;
    setShowProfileModal(true);
    setLoadingInvestigadorProfile(true);
    setInvestigadorProfile(null);
    try {
      const response = await axios.get(`/api/investigadores/${inv.id}/perfil-ia`);
      setInvestigadorProfile(response.data);
    } catch (error) {
      setInvestigadorProfile({
        investigador_nombre: inv.nombre,
        resumen: "",
        advertencias: [
          "No se pudo generar la ficha del investigador.",
          error.response?.data?.detail || error.message,
        ],
        temas_principales: [],
        fortalezas: [],
        metodologias_clave: [],
        enfoque_aplicacion: [],
        ods_principales: [],
        campo_cine_principal: "",
        proyeccion_investigacion: "",
        publicaciones_analizadas: 0,
        fuente: "heuristica",
      });
    } finally {
      setLoadingInvestigadorProfile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newInvestigador.universidad_id) {
      setError("Selecciona la universidad de afiliación (IES) del investigador.");
      return;
    }
    try {
      const normalizedOpenAlexId = normalizeOpenAlexAuthorId(newInvestigador.open_alex_id);
      if (normalizedOpenAlexId && !/^A\d+$/i.test(normalizedOpenAlexId)) {
        setError("El OpenAlex ID debe verse como A123456789 o como una URL completa de OpenAlex.");
        return;
      }

      const payload = {
        ...newInvestigador,
        universidad_id: Number(newInvestigador.universidad_id),
        open_alex_id: normalizedOpenAlexId || null,
      };
      if (isEditing) {
        await axios.put(`/api/investigadores/${editId}`, payload);
      } else {
        await axios.post("/api/investigadores/", payload);
      }
      fetchInvestigadores();
      resetForm();
      setShowFormModal(false);
    } catch (error) {
      setError("Error al guardar investigador: " + error.message);
    }
  };

  const handleEdit = (inv) => {
    setNewInvestigador({
      nombre: inv.nombre,
      genero: inv.genero,
      institucion: inv.institucion,
      universidad_id: inv.universidad_id != null ? inv.universidad_id : "",
      open_alex_id: inv.open_alex_id || "",
      orcid: inv.orcid,
      email: inv.email,
      tipo_puesto: inv.tipo_puesto || "",
      departamento: inv.departamento || "",
    });
    setEditId(inv.id);
    setIsEditing(true);
    setShowFormModal(true);
  };

  const handleDelete = async (id) => {
    const accepted = await confirm({
      title: 'Eliminar investigador',
      message: '¿Estás seguro de eliminar este investigador?',
      details: 'Esta acción eliminará su registro del listado actual.',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      variation: 'danger',
      badge: 'Eliminar',
      icon: 'person_remove',
    });
    if (!accepted) return;

    try {
      await axios.delete(`/api/investigadores/${id}`);
      fetchInvestigadores();
    } catch (error) {
      setError("Error al eliminar investigador: " + error.message);
    }
  };

  const resetForm = () => {
    setNewInvestigador({
      nombre: "",
      genero: "",
      institucion: "",
      universidad_id: "",
      open_alex_id: "",
      orcid: "",
      email: "",
      tipo_puesto: "",
      departamento: "",
    });
    setIsEditing(false);
    setEditId(null);
  };
  // Paginación
  const paginatedInvestigadores = filteredInvestigadores.slice(
    currentPage * mainPageSize,
    (currentPage + 1) * mainPageSize
  );
  const totalMainPages = Math.ceil(filteredInvestigadores.length / mainPageSize);

  const paginatedPublicaciones = selectedPublicaciones.slice(
    modalPage * pageSize,
    (modalPage + 1) * pageSize
  );
  const totalModalPages = Math.ceil(selectedPublicaciones.length / pageSize);

  // Generar avatar con iniciales
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  // Colores de avatar basados en el nombre
  const getAvatarColor = (name) => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-purple-500 to-purple-600',
      'from-green-500 to-green-600',
      'from-orange-500 to-orange-600',
      'from-pink-500 to-pink-600',
      'from-teal-500 to-teal-600',
      'from-indigo-500 to-indigo-600',
      'from-red-500 to-red-600',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const cleanProfileItems = (items) =>
    (Array.isArray(items) ? items : [])
      .map((item) => String(item || "").trim())
      .filter(Boolean);

  return (
    <>
      {/* Header 
      <header className="bg-surface-light dark:bg-surface-dark shadow-sm border-b border-border-light dark:border-border-dark">
        <div className="max-w-1xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h2 className="text-xl font-bold leading-7 text-slate-900 dark:text-white sm:truncate">Investigadores</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Explora y gestiona los perfiles de investigadores</p>
            </div>
          </div>
        </div>
      </header>*/}
      
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex gap-8">
        
        {/* Sidebar Filters */}
        <aside className="w-72 hidden lg:flex flex-col gap-6 shrink-0 h-[calc(100vh-100px)] sticky top-6 overflow-y-auto pb-8">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Investigadores</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {filteredInvestigadores.length} perfiles activos
            </p>
          </div>
          
          {/* Botón Nuevo - Solo visible para Admin/SuperAdmin */}
          {canCreateEdit() && (
            <button
              onClick={() => {
                resetForm();
                setShowFormModal(true);
              }}
              className="w-full py-3 bg-primary hover:bg-primary/90 text-white font-bold rounded-lg shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2"
            >
              <span className="material-icons-round">person_add</span>
              Nuevo Investigador
            </button>
          )}
          
          <div className="h-px bg-slate-200 dark:bg-slate-800 w-full"></div>
          
          {/* Búsqueda */}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <span className="material-icons-round text-[20px]">search</span>
            </span>
            <input
              type="text"
              className="w-full h-10 pl-10 pr-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              placeholder="Buscar investigadores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="h-px bg-slate-200 dark:bg-slate-800 w-full"></div>
          
          {/* Filter: Institution */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900 dark:text-white">Institución</h3>
              {selectedInstitution && (
                <button 
                  onClick={() => setSelectedInstitution("")}
                  className="text-xs text-primary hover:underline"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={!selectedInstitution}
                  onChange={() => setSelectedInstitution("")}
                  className="rounded border-slate-300 dark:border-slate-600 bg-transparent text-primary focus:ring-0 focus:ring-offset-0"
                />
                <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-primary transition-colors flex-1 truncate font-medium">
                  Todas las universidades
                </span>
                <span className="text-xs text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                  {investigadores.length}
                </span>
              </label>
              {institutionCounts.map((inst) => (
                <label key={inst.name} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedInstitution === inst.name}
                    onChange={() => handleInstitutionFilter(inst.name)}
                    className="rounded border-slate-300 dark:border-slate-600 bg-transparent text-primary focus:ring-0 focus:ring-offset-0"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-primary transition-colors flex-1 truncate">
                    {inst.name}
                  </span>
                  <span className="text-xs text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                    {inst.count}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="h-px bg-slate-200 dark:bg-slate-800 w-full"></div>
          
          {/* Filter: Tipo de Puesto */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900 dark:text-white">Tipo de Puesto</h3>
              {selectedTipoPuesto.length > 0 && (
                <button 
                  onClick={() => setSelectedTipoPuesto([])}
                  className="text-xs text-primary hover:underline"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
              {false && (
                <p className="text-sm text-slate-400 dark:text-slate-500">
                  Selecciona una instituciÃ³n para ver sus tipos de puesto.
                </p>
              )}
              {!selectedInstitution && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Mostrando tipos de puesto de todas las universidades.
                </p>
              )}
              {tipoPuestoCounts.length === 0 && (
                <p className="text-sm text-slate-400 dark:text-slate-500">
                  No hay tipos de puesto cargados para esta institución.
                </p>
              )}
              {tipoPuestoCounts.map((tp) => (
                <label key={tp.name} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedTipoPuesto.includes(tp.name)}
                    onChange={() => handleTipoPuestoFilter(tp.name)}
                    className="rounded border-slate-300 dark:border-slate-600 bg-transparent text-primary focus:ring-0 focus:ring-offset-0"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-primary transition-colors flex-1 truncate">
                    {tp.name}
                  </span>
                  <span className="text-xs text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                    {tp.count}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="h-px bg-slate-200 dark:bg-slate-800 w-full"></div>
          
          {/* Filter: Departamento */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900 dark:text-white">Departamento</h3>
              {selectedDepartamento.length > 0 && (
                <button 
                  onClick={() => setSelectedDepartamento([])}
                  className="text-xs text-primary hover:underline"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
              {false && (
                <p className="text-sm text-slate-400 dark:text-slate-500">
                  Selecciona una instituciÃ³n para ver sus departamentos.
                </p>
              )}
              {!selectedInstitution && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Mostrando departamentos de todas las universidades.
                </p>
              )}
              {departamentoGroups.length === 0 && (
                <p className="text-sm text-slate-400 dark:text-slate-500">
                  No hay departamentos cargados para esta institución.
                </p>
              )}
              {departamentoGroups.map((group) => (
                <div key={group.campus} className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {group.campus}
                  </p>
                  {group.items.map((dep) => (
                    <label key={dep.name} className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedDepartamento.includes(dep.name)}
                        onChange={() => handleDepartamentoFilter(dep.name)}
                        className="rounded border-slate-300 dark:border-slate-600 bg-transparent text-primary focus:ring-0 focus:ring-offset-0"
                      />
                      <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-primary transition-colors flex-1 truncate">
                        {dep.label}
                      </span>
                      <span className="text-xs text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                        {dep.count}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="h-px bg-slate-200 dark:bg-slate-800 w-full"></div>

          {/* Filter: Género */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900 dark:text-white">Género</h3>
              {selectedGeneros.length > 0 && (
                <button
                  onClick={() => setSelectedGeneros([])}
                  className="text-xs text-primary hover:underline"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {!selectedInstitution && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Mostrando género de todas las universidades.
                </p>
              )}
              {generoCounts.map((g) => (
                <label key={g.name} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedGeneros.includes(g.name)}
                    onChange={() => handleGenderFilter(g.name)}
                    className="rounded border-slate-300 dark:border-slate-600 bg-transparent text-primary focus:ring-0 focus:ring-offset-0"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-primary transition-colors truncate">
                    {g.name}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    g.name === "Femenino"
                      ? "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-200 border border-rose-200/60 dark:border-rose-500/20"
                      : g.name === "Masculino"
                        ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-200 border border-blue-200/60 dark:border-blue-500/20"
                        : g.name === "No especificado"
                          ? "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                  }`}>
                    {g.count}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="h-px bg-slate-200 dark:bg-slate-800 w-full"></div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900 dark:text-white">Publicaciones</h3>
              {selectedPublicationStatus && (
                <button
                  onClick={() => setSelectedPublicationStatus("")}
                  className="text-xs text-primary hover:underline"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedPublicationStatus === "con"}
                  onChange={() =>
                    setSelectedPublicationStatus((prev) => (prev === "con" ? "" : "con"))
                  }
                  className="rounded border-slate-300 dark:border-slate-600 bg-transparent text-primary focus:ring-0 focus:ring-offset-0"
                />
                <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-primary transition-colors flex-1 truncate">
                  Con publicaciones
                </span>
                <span className="text-xs text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                  {publicationStatusCounts.con}
                </span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedPublicationStatus === "sin"}
                  onChange={() =>
                    setSelectedPublicationStatus((prev) => (prev === "sin" ? "" : "sin"))
                  }
                  className="rounded border-slate-300 dark:border-slate-600 bg-transparent text-primary focus:ring-0 focus:ring-offset-0"
                />
                <span className="text-sm text-slate-600 dark:text-slate-300 group-hover:text-primary transition-colors flex-1 truncate">
                  Sin publicaciones
                </span>
                <span className="text-xs text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                  {publicationStatusCounts.sin}
                </span>
              </label>
            </div>
          </div>
        </aside>
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col gap-6">
          
          {/* Error Alert */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center">
              <span className="material-icons-round text-red-500 mr-3">error</span>
              <span className="text-red-700 dark:text-red-300 flex-1">{error}</span>
              <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                <span className="material-icons-round">close</span>
              </button>
            </div>
          )}
          
          {/* Top Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-[#182635] p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 dark:text-slate-400">Mostrando</span>
              <span className="text-sm font-bold text-slate-900 dark:text-white">{paginatedInvestigadores.length}</span>
              <span className="text-sm text-slate-500 dark:text-slate-400">de</span>
              <span className="text-sm font-bold text-slate-900 dark:text-white">{filteredInvestigadores.length}</span>
              <span className="text-sm text-slate-500 dark:text-slate-400">investigadores</span>
            </div>
            <div className="flex items-center gap-3 ml-auto">
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-500 dark:text-slate-400">Ordenar:</label>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-transparent border-none text-sm font-bold text-slate-900 dark:text-white focus:ring-0 cursor-pointer p-0 pr-8"
                >
                  <option value="nombre">Nombre (A-Z)</option>
                  <option value="institucion">Institución</option>
                  <option value="tipo_puesto">Tipo de Puesto</option>
                  <option value="departamento">Departamento</option>
                </select>
              </div>
              <div className="h-4 w-px bg-slate-200 dark:bg-slate-700"></div>
              <div className="flex bg-slate-100 dark:bg-slate-800 rounded p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-1 rounded transition-colors ${viewMode === "grid" ? "bg-white dark:bg-slate-700 text-primary shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
                >
                  <span className="material-icons-round text-[20px]">grid_view</span>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-1 rounded transition-colors ${viewMode === "list" ? "bg-white dark:bg-slate-700 text-primary shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
                >
                  <span className="material-icons-round text-[20px]">view_list</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Mobile Search */}
          <div className="lg:hidden relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <span className="material-icons-round text-[20px]">search</span>
            </span>
            <input
              type="text"
              className="w-full h-12 pl-10 pr-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary"
              placeholder="Buscar investigadores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Grid View */}
          {viewMode === "grid" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {paginatedInvestigadores.map((inv) => {
                const publicationCount = getPublicationCount(inv);
                const hasZeroPublications = publicationCount === 0;

                return (
                <article 
                  key={inv.id}
                  className={`group rounded-xl border p-5 transition-all flex flex-col gap-4 relative overflow-hidden ${
                    hasZeroPublications
                      ? "bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800/70 hover:border-red-300 dark:hover:border-red-700 hover:shadow-lg hover:shadow-red-500/10"
                      : "bg-white dark:bg-[#182635] border-slate-200 dark:border-slate-800 hover:border-primary/50 dark:hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5"
                  }`}
                >
                  {/* Bookmark button - Solo visible para Admin/SuperAdmin */}
                  {canCreateEdit() && (
                    <div className="absolute top-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button 
                        onClick={() => handleEdit(inv)}
                        className="text-slate-400 hover:text-amber-500 transition-colors"
                        title="Editar"
                      >
                        <span className="material-icons-round text-[20px]">edit</span>
                      </button>
                      <button 
                        onClick={() => handleDelete(inv.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        title="Eliminar"
                      >
                        <span className="material-icons-round text-[20px]">delete</span>
                      </button>
                    </div>
                  )}
                  
                  {/* Profile */}
                  <div className="flex items-start gap-4">
                    {/* Avatar with Initials */}
                    <div className="size-16 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold text-xl border-2 border-white dark:border-slate-700">
                      {getInitials(inv.nombre)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white leading-tight group-hover:text-primary transition-colors cursor-pointer truncate">
                        {inv.nombre}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {inv.tipo_puesto && (
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                            {inv.tipo_puesto}
                          </span>
                        )}
                        {inv.departamento && (
                          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                            {inv.departamento}
                          </span>
                        )}
                        {!inv.tipo_puesto && !inv.departamento && (
                          <span className="text-sm text-slate-400 dark:text-slate-500">
                            Sin información adicional
                          </span>
                        )}
                      </div>
                      {inv.institucion && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded w-fit max-w-full">
                          <span className="material-icons-round text-[14px]">apartment</span>
                          <span className="truncate">{inv.institucion}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded w-fit max-w-full">
                        <span className="material-icons-round text-[14px]">badge</span>
                        <span className="truncate">
                          OpenAlex ID: {inv.open_alex_id || "Sin OpenAlex ID"}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Info Grid */}
                  <div className={`grid grid-cols-2 gap-2 py-3 border-y ${
                    hasZeroPublications
                      ? "border-red-100 dark:border-red-900/40"
                      : "border-slate-100 dark:border-slate-700/50"
                  }`}>
                    <div className="flex flex-col items-center justify-center p-2 rounded bg-slate-50 dark:bg-slate-800/30">
                      <span
                        className={`material-icons-round text-xl ${
                          inv.genero === "Femenino"
                            ? "text-rose-500"
                            : inv.genero === "Masculino"
                              ? "text-blue-500"
                              : "text-slate-400"
                        }`}
                      >
                        {inv.genero === "Femenino"
                          ? "female"
                          : inv.genero === "Masculino"
                            ? "male"
                            : "person"}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">{inv.genero || "N/A"}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 rounded bg-slate-50 dark:bg-slate-800/30">
                      <span className="material-icons-round text-primary text-xl">email</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">{inv.email ? "Si" : "N/A"}</span>
                    </div>
                  </div>
                  
                  <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                    hasZeroPublications
                      ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                      : "bg-slate-50 text-slate-600 dark:bg-slate-800/40 dark:text-slate-300"
                  }`}>
                    <span className="text-xs font-semibold uppercase tracking-wide">Publicaciones</span>
                    <span className="text-sm font-bold">{publicationCount}</span>
                  </div>

                  {/* Tags */}
                  {inv.orcid && (
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded font-medium">
                        ORCID: {inv.orcid.slice(-8)}...
                      </span>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="mt-auto grid grid-cols-1 gap-2">
                    <button
                      onClick={() => fetchPublicacionesByInvestigador(inv)}
                      className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                        hasZeroPublications
                          ? "border border-red-200 text-red-700 dark:border-red-700 dark:text-red-300 hover:bg-red-600 hover:text-white hover:border-red-600"
                          : "border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-primary hover:text-white hover:border-primary"
                      }`}
                    >
                      Ver Publicaciones
                      <span className="material-icons-round text-[16px]">article</span>
                    </button>
                    <button
                      onClick={() => handleOpenInvestigadorProfile(inv)}
                      className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 border border-violet-200 text-violet-700 hover:bg-violet-50 dark:border-violet-800/60 dark:text-violet-300 dark:hover:bg-violet-900/20"
                    >
                      Perfil con IA
                      <span className="material-icons-round text-[16px]">psychology</span>
                    </button>
                  </div>
                </article>
                );
              })}
            </div>
          )}
          
          {/* List View */}
          {viewMode === "list" && (
            <div className="bg-white dark:bg-[#182635] rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Institución</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Tipo de Puesto</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Departamento</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {paginatedInvestigadores.map((inv) => {
                    const publicationCount = getPublicationCount(inv);
                    const hasZeroPublications = publicationCount === 0;

                    return (
                    <tr
                      key={inv.id}
                      className={`transition-colors ${
                        hasZeroPublications
                          ? "bg-red-50/80 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-900/20"
                          : "hover:bg-slate-50 dark:hover:bg-slate-800/30"
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="size-10 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-semibold text-sm mr-3">
                            {getInitials(inv.nombre)}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900 dark:text-white">{inv.nombre}</div>
                            <div className="text-sm text-slate-500">{inv.email || "Sin email"}</div>
                            <div className="text-xs text-slate-400">
                              OpenAlex ID: {inv.open_alex_id || "Sin OpenAlex ID"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{inv.institucion || "N/A"}</td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{inv.tipo_puesto || "N/A"}</td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                        <div className="flex items-center justify-between gap-3">
                          <span>{inv.departamento || "N/A"}</span>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                            hasZeroPublications
                              ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                          }`}>
                            {publicationCount} pubs
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center gap-2">
                          {canCreateEdit() && (
                            <>
                              <button
                                onClick={() => handleEdit(inv)}
                                className="p-2 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                                title="Editar"
                              >
                                <span className="material-icons-round">edit</span>
                              </button>
                              <button
                                onClick={() => handleDelete(inv.id)}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <span className="material-icons-round">delete</span>
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => fetchPublicacionesByInvestigador(inv)}
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title="Ver publicaciones"
                          >
                            <span className="material-icons-round">article</span>
                          </button>
                          <button
                            onClick={() => handleOpenInvestigadorProfile(inv)}
                            className="p-2 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-lg transition-colors"
                            title="Perfil con IA"
                          >
                            <span className="material-icons-round">psychology</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Empty State */}
          {filteredInvestigadores.length === 0 && (
            <div className="text-center py-16 bg-white dark:bg-[#182635] rounded-xl border border-slate-200 dark:border-slate-800">
              <span className="material-icons-round text-slate-300 dark:text-slate-600 text-6xl">person_search</span>
              <p className="text-slate-500 dark:text-slate-400 mt-4 text-lg">No se encontraron investigadores</p>
              <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">Intenta ajustar los filtros de búsqueda</p>
            </div>
          )}
          
          {/* Pagination */}
          {totalMainPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors disabled:opacity-50"
              >
                <span className="material-icons-round">chevron_left</span>
              </button>
              
              {Array.from({ length: Math.min(5, totalMainPages) }, (_, i) => {
                let pageNum;
                if (totalMainPages <= 5) {
                  pageNum = i;
                } else if (currentPage < 3) {
                  pageNum = i;
                } else if (currentPage > totalMainPages - 4) {
                  pageNum = totalMainPages - 5 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`h-10 w-10 flex items-center justify-center rounded-lg font-bold transition-colors ${
                      currentPage === pageNum
                        ? "bg-primary text-white shadow-lg shadow-primary/20"
                        : "border border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary"
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
              
              {totalMainPages > 5 && currentPage < totalMainPages - 3 && (
                <>
                  <span className="text-slate-400 px-2">...</span>
                  <button
                    onClick={() => setCurrentPage(totalMainPages - 1)}
                    className="h-10 w-10 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors"
                  >
                    {totalMainPages}
                  </button>
                </>
              )}
              
              <button
                onClick={() => setCurrentPage(p => Math.min(totalMainPages - 1, p + 1))}
                disabled={currentPage >= totalMainPages - 1}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors disabled:opacity-50"
              >
                <span className="material-icons-round">chevron_right</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de Publicaciones */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}></div>
            <div className="relative bg-white dark:bg-[#182635] rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-gradient-to-r from-primary/5 to-transparent">
                <div className="flex items-center gap-4">
                  {selectedInvestigador && (
                    <div className={`size-12 rounded-full bg-gradient-to-br ${getAvatarColor(selectedInvestigador.nombre)} flex items-center justify-center text-white font-bold`}>
                      {getInitials(selectedInvestigador.nombre)}
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      Publicaciones de {selectedInvestigador?.nombre}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {selectedPublicaciones.length} publicaciones encontradas
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canCreateEdit() && selectedInvestigador && (
                    <button
                      onClick={() => handleAddPublication(selectedInvestigador)}
                      disabled={!hasOpenAlexIdentifier(selectedInvestigador)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
                    >
                      <span className="material-icons-round text-base">add_circle</span>
                      Añadir publicación
                    </button>
                  )}
                  <button 
                    onClick={() => setShowModal(false)} 
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <span className="material-icons-round">close</span>
                  </button>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[55vh]">
                {paginatedPublicaciones.length > 0 ? (
                  <div className="space-y-4">
                    {paginatedPublicaciones.map((pub, idx) => (
                      <div key={pub.id || idx} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-primary/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            {/* Titulo */}
                            <h4 className="font-semibold text-slate-900 dark:text-white leading-tight">
                              {pub.titulo}
                            </h4>
                            
                            {/* Metadata principal */}
                            <div className="flex flex-wrap items-center gap-3 mt-2">
                              <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                <span className="material-icons-round text-[14px]">calendar_today</span>
                                {pub.anio || "N/A"}
                              </span>
                              {pub.journal && (
                                <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                  <span className="material-icons-round text-[14px]">menu_book</span>
                                  {pub.journal}
                                </span>
                              )}
                              {pub.tipo && (
                                <span className="text-xs bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full capitalize">
                                  {pub.tipo}
                                </span>
                              )}
                              {pub.citations_count > 0 && (
                                <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                  <span className="material-icons-round text-[14px]">format_quote</span>
                                  {pub.citations_count} citas
                                </span>
                              )}
                            </div>
                            
                            {/* Badges */}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {pub.is_open_access && (
                                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <span className="material-icons-round text-[12px]">lock_open</span>
                                  Open Access
                                  {pub.oa_status && ` (${pub.oa_status})`}
                                </span>
                              )}
                              {pub.is_top_10_percent && (
                                <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <span className="material-icons-round text-[12px]">star</span>
                                  Top 10%
                                </span>
                              )}
                              {pub.source && (
                                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full">
                                  {pub.source}
                                </span>
                              )}
                            </div>
                            
                            {/* DOI y Publisher */}
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500 dark:text-slate-400">
                              {pub.doi && (
                                <a 
                                  href={`https://doi.org/${pub.doi}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:text-primary transition-colors"
                                >
                                  DOI: {pub.doi}
                                </a>
                              )}
                              {pub.publisher && (
                                <span>{pub.publisher}</span>
                              )}
                              {(pub.volume || pub.issue) && (
                                <span>
                                  {pub.volume && `Vol. ${pub.volume}`}
                                  {pub.issue && `, No. ${pub.issue}`}
                                  {pub.first_page && `, pp. ${pub.first_page}`}
                                  {pub.last_page && `-${pub.last_page}`}
                                </span>
                              )}
                            </div>
                            
                            {/* Abstract (truncado) */}
                            {pub.abstract && (
                              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 line-clamp-2">
                                {pub.abstract}
                              </p>
                            )}
                            
                            {/* Keywords */}
                            {pub.keywords && pub.keywords.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {pub.keywords.slice(0, 5).map((kw, i) => (
                                  <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                    {kw}
                                  </span>
                                ))}
                                {pub.keywords.length > 5 && (
                                  <span className="text-xs text-slate-400">+{pub.keywords.length - 5} más</span>
                                )}
                              </div>
                            )}
                            
                            {/* FWCI */}
                            {pub.fwci !== null && pub.fwci !== undefined && (
                              <div className="mt-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  pub.fwci > 1 
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' 
                                    : pub.fwci === 0 
                                      ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                }`}>
                                  <span className="material-icons-round text-[10px] mr-1">analytics</span>
                                  FWCI: {pub.fwci.toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Enlaces externos */}
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            {canCreateEdit() && pub.id && (
                              <button
                                onClick={() => handleDeletePublication(pub)}
                                disabled={deletingPublicationId === pub.id}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                                title="Eliminar publicación"
                              >
                                <span className="material-icons-round">
                                  {deletingPublicationId === pub.id ? 'hourglass_top' : 'delete'}
                                </span>
                              </button>
                            )}
                            {pub.doi && (
                              <a
                                href={`https://doi.org/${pub.doi}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                title="Ver DOI"
                              >
                                <span className="material-icons-round">open_in_new</span>
                              </a>
                            )}
                            {pub.oa_url && (
                              <a
                                href={pub.oa_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                title="Acceso abierto"
                              >
                                <span className="material-icons-round">download</span>
                              </a>
                            )}
                            {pub.external_url && !pub.doi && (
                              <a
                                href={pub.external_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                title="Ver publicación"
                              >
                                <span className="material-icons-round">link</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <span className="material-icons-round text-slate-300 dark:text-slate-600 text-5xl">article</span>
                    <p className="text-slate-500 dark:text-slate-400 mt-3">No hay publicaciones registradas</p>
                  </div>
                )}
              </div>
              
              {/* Modal Pagination */}
              {totalModalPages > 1 && (
                <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/30">
                  <button
                    onClick={() => setModalPage(p => Math.max(0, p - 1))}
                    disabled={modalPage === 0}
                    className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 disabled:opacity-50 hover:bg-white dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                  >
                    <span className="material-icons-round text-sm">chevron_left</span>
                    Anterior
                  </button>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Página {modalPage + 1} de {totalModalPages}
                  </span>
                  <button
                    onClick={() => setModalPage(p => Math.min(totalModalPages - 1, p + 1))}
                    disabled={modalPage >= totalModalPages - 1}
                    className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 disabled:opacity-50 hover:bg-white dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                  >
                    Siguiente
                    <span className="material-icons-round text-sm">chevron_right</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showProfileModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/55 backdrop-blur-sm"
              onClick={() => setShowProfileModal(false)}
            ></div>
            <div className="relative bg-white dark:bg-[#182635] rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-4 bg-gradient-to-r from-violet-500/10 to-transparent">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-violet-100 dark:bg-violet-900/30 px-3 py-1 text-xs font-semibold text-violet-700 dark:text-violet-300">
                    <span className="material-icons-round text-sm">psychology</span>
                    Perfil del investigador
                  </div>
                  <h3 className="mt-3 text-lg font-bold text-slate-900 dark:text-white">
                    {selectedInvestigador?.nombre}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Ficha informativa generada a partir de títulos, concepts y topics de las publicaciones guardadas.
                  </p>
                </div>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <span className="material-icons-round">close</span>
                </button>
              </div>

              <div className="p-6 max-h-[70vh] overflow-y-auto">
                {loadingInvestigadorProfile ? (
                  <div className="py-14 text-center">
                    <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600"></div>
                    <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                      Analizando publicaciones del investigador...
                    </p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {(() => {
                      const temas = cleanProfileItems(investigadorProfile?.temas_principales);
                      const fortalezas = cleanProfileItems(investigadorProfile?.fortalezas);
                      const metodologias = cleanProfileItems(investigadorProfile?.metodologias_clave);
                      const enfoques = cleanProfileItems(investigadorProfile?.enfoque_aplicacion);
                      const ods = cleanProfileItems(investigadorProfile?.ods_principales);

                      const TagSection = ({ title, icon, values, tone = 'violet' }) => (
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
                          <div className="flex items-center gap-2">
                            <span className={`material-icons-round text-base ${tone === 'emerald' ? 'text-emerald-600 dark:text-emerald-300' : 'text-violet-600 dark:text-violet-300'}`}>{icon}</span>
                            <h4 className="text-sm font-bold text-slate-900 dark:text-white">{title}</h4>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {values.length > 0 ? values.map((item) => (
                              <span
                                key={`${title}-${item}`}
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                                  tone === 'emerald'
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200'
                                    : 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200'
                                }`}
                              >
                                {item}
                              </span>
                            )) : (
                              <span className="text-sm text-slate-500 dark:text-slate-400">Sin datos suficientes</span>
                            )}
                          </div>
                        </div>
                      );

                      return (
                        <>
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Publicaciones analizadas</p>
                        <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                          {investigadorProfile?.publicaciones_analizadas || 0}
                        </p>
                      </div>
                      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Campo CINE principal</p>
                        <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-white">
                          {investigadorProfile?.campo_cine_principal || "No identificado"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Fuente del análisis</p>
                        <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-white capitalize">
                          {investigadorProfile?.fuente || "heuristica"}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/30 p-5">
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white">Resumen</h4>
                      <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
                        {investigadorProfile?.resumen || "No se pudo generar un resumen para este investigador."}
                      </p>
                    </div>

                    <div className="grid gap-4 xl:grid-cols-2">
                      <TagSection title="Temas principales" icon="local_offer" values={temas} />
                      <TagSection title="Fortalezas" icon="workspace_premium" values={fortalezas} />
                      <TagSection title="Metodologías clave" icon="schema" values={metodologias} />
                      <TagSection title="ODS relacionados" icon="eco" values={ods} tone="emerald" />
                    </div>

                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/30 p-5">
                      <div className="flex items-center gap-2">
                        <span className="material-icons-round text-base text-violet-600 dark:text-violet-300">psychology</span>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white">Enfoque de aplicación</h4>
                      </div>
                      <div className="mt-3">
                        {enfoques.length > 0 ? (
                          <div className="grid gap-3 md:grid-cols-2">
                            {enfoques.map((item) => (
                              <div
                                key={`enfoque-${item}`}
                                className="rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-sm font-medium text-violet-800 dark:border-violet-900/40 dark:bg-violet-900/20 dark:text-violet-100"
                              >
                                {item}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-400">Sin datos suficientes</span>
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/30 p-5">
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white">Proyección de investigación</h4>
                      <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
                        {investigadorProfile?.proyeccion_investigacion || "No se pudo inferir una proyección clara con la evidencia disponible."}
                      </p>
                    </div>

                    {Array.isArray(investigadorProfile?.advertencias) && investigadorProfile.advertencias.length > 0 && (
                      <div className="rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 p-4">
                        <div className="flex items-start gap-2">
                          <span className="material-icons-round text-amber-600 dark:text-amber-300 text-base">info</span>
                          <div className="space-y-2">
                            {investigadorProfile.advertencias.map((warning, idx) => (
                              <p key={`warning-${idx}`} className="text-sm text-amber-800 dark:text-amber-200">
                                {warning}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Formulario */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowFormModal(false)}></div>
            <div className="relative bg-white dark:bg-[#182635] rounded-2xl shadow-2xl max-w-lg w-full">
              {/* Form Header */}
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <span className="material-icons-round text-primary">{isEditing ? "edit" : "person_add"}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      {isEditing ? "Editar Investigador" : "Nuevo Investigador"}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {isEditing ? "Modifica los datos del investigador" : "Completa los datos para registrar"}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Form Body */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    placeholder="Ej: Dr. Juan Pérez"
                    value={newInvestigador.nombre}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Género
                  </label>
                  <select
                    name="genero"
                    value={newInvestigador.genero}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Seleccionar género</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Afiliación IES (CEDIA) *
                  </label>
                  <select
                    name="universidad_id"
                    value={
                      newInvestigador.universidad_id === "" || newInvestigador.universidad_id == null
                        ? ""
                        : String(newInvestigador.universidad_id)
                    }
                    onChange={(e) => {
                      const id = e.target.value;
                      const uni = universidadesApi.find((u) => String(u.id) === id);
                      setNewInvestigador((prev) => ({
                        ...prev,
                        universidad_id: id ? parseInt(id, 10) : "",
                        institucion: uni ? (uni.name || uni.short_name || "") : prev.institucion,
                      }));
                    }}
                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  >
                    <option value="">Seleccionar universidad</option>
                    {universidadesApi.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name || u.short_name || `ID ${u.id}`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Define la afiliación en el sistema (tabla investigador_afiliacion).
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    OpenAlex ID
                  </label>
                  <input
                    type="text"
                    name="open_alex_id"
                    placeholder="Ej: A5085173850 o https://openalex.org/A5085173850"
                    value={newInvestigador.open_alex_id}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Puedes pegar el ID del autor o la URL completa de OpenAlex.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    ORCID
                  </label>
                  <input
                    type="text"
                    name="orcid"
                    placeholder="Ej: 0000-0001-2345-6789"
                    value={newInvestigador.orcid}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Ej: investigador@universidad.edu"
                    value={newInvestigador.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Tipo de Puesto
                    </label>
                    <select
                      name="tipo_puesto"
                      value={newInvestigador.tipo_puesto}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Seleccionar puesto</option>
                      {isEditing &&
                        newInvestigador.tipo_puesto &&
                        !tipoPuestoOpcionesFormulario.some(
                          (label) => normalizeTipoPuesto(label) === normalizeTipoPuesto(newInvestigador.tipo_puesto)
                        ) && (
                          <option value={newInvestigador.tipo_puesto}>
                            {newInvestigador.tipo_puesto} (actual)
                          </option>
                        )}
                      {tipoPuestoOpcionesFormulario.map((label) => (
                        <option key={label} value={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Departamento
                    </label>
                    <input
                      type="text"
                      name="departamento"
                      placeholder="Ej: Departamento de Ciencias"
                      value={newInvestigador.departamento}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
                
                {/* Form Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowFormModal(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium shadow-lg shadow-primary/20"
                  >
                    {isEditing ? "Actualizar" : "Guardar"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

export default Investigadores;
